import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  DollarSign,
  Calendar,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Analytics, AnalyticsPeriod, type AppointmentStatus, fetchAdminAnalytics } from '../api';
import { useTheme } from '../context/ThemeContext';
import { CHART_PALETTE, STATUS_CHART } from '../lib/statusColors';

/**
 * Pie slice labels. Rendered by hand rather than with Recharts' string-returning
 * shorthand, which paints the text in a default near-black that disappears on a
 * dark surface — this way the fill is explicit.
 */
function renderPieLabel(fill: string) {
  return function PieLabel({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    name,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    outerRadius: number;
    percent: number;
    name: string;
  }) {
    const radians = Math.PI / 180;
    const radius = outerRadius + 18;
    const x = cx + radius * Math.cos(-midAngle * radians);
    const y = cy + radius * Math.sin(-midAngle * radians);
    return (
      <text
        x={x}
        y={y}
        fill={fill}
        fontSize={11}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
}

const PERIOD_LABELS: Record<AnalyticsPeriod, string> = {
  week: 'This Week',
  month: 'This Month',
  quarter: 'This Quarter',
};

/**
 * Percent change vs the previous period.
 *
 * `null` and `0` are different answers and render differently: the API returns
 * `null` only when the previous period had nothing to divide by, so there is no
 * comparison to show and the badge is omitted. A real `0` means the figure
 * genuinely held flat — worth stating, and not the same as "we can't say".
 */
function TrendBadge({ value }: { value: number | null }) {
  if (value === null) return null;
  if (value === 0) {
    return (
      <span className="bg-muted text-fg-muted inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
        <Minus className="h-3 w-3" />
        0%
      </span>
    );
  }
  const isPositive = value > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
        isPositive ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'
      }`}
    >
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(value).toFixed(0)}%
    </span>
  );
}

export default function AdminAnalytics() {
  // Recharts styles via hex props, not classes, so the chart colors have to be
  // picked in JS off the resolved theme.
  const { resolved } = useTheme();
  const statusColor = STATUS_CHART[resolved];
  const chart = CHART_PALETTE[resolved];

  const [period, setPeriod] = useState<AnalyticsPeriod>('month');
  const [data, setData] = useState<Analytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Bumped by "Try again" to re-run the effect without duplicating the fetch.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');
    fetchAdminAnalytics(period)
      .then((analytics) => {
        if (!cancelled) setData(analytics);
      })
      .catch(() => {
        // A slower earlier request must not clear a newer period's error/data.
        if (!cancelled) setError("Couldn't load analytics.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period, reloadKey]);

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-fg text-lg font-bold">Analytics</h2>
        <div className="bg-muted flex rounded-lg p-1">
          {(['week', 'month', 'quarter'] as AnalyticsPeriod[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                period === p
                  ? 'text-accent bg-surface shadow-sm'
                  : 'text-fg-muted hover:text-fg-secondary'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="border-default bg-surface flex flex-col items-center gap-3 rounded-xl border py-16 shadow-sm">
          <AlertCircle className="text-fg-subtle h-6 w-6" />
          <p className="text-fg-muted text-sm">{error}</p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="text-accent border-default hover:bg-canvas rounded-lg border px-3 py-1.5 text-xs font-medium"
          >
            Try again
          </button>
        </div>
      ) : !data ? (
        <div className="border-default bg-surface flex items-center justify-center rounded-xl border py-16 shadow-sm">
          <Loader2 className="text-fg-subtle h-6 w-6 animate-spin" />
        </div>
      ) : (
        // Dim the whole board while a new period loads, so stale figures never
        // read as the newly selected period's.
        <div className={`space-y-6 transition-opacity ${isLoading ? 'opacity-50' : ''}`}>
          {/* Revenue cards */}
          <div>
            <h3 className="text-fg-muted mb-3 text-sm font-semibold tracking-wide uppercase">
              Revenue
            </h3>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4" />
                  Today
                </div>
                <p className="text-fg mt-1 text-2xl font-bold">${data.revenue.today.toFixed(0)}</p>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4" />
                  {data.periodLabel}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-fg text-2xl font-bold">${data.revenue.period.toFixed(0)}</p>
                  <TrendBadge value={data.revenue.trend} />
                </div>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted text-sm">Avg / Appointment</div>
                <p className="text-fg mt-1 text-2xl font-bold">
                  ${data.revenue.avgPerAppointment.toFixed(0)}
                </p>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted text-sm">Previous Period</div>
                <p className="text-fg-subtle mt-1 text-2xl font-bold">
                  ${data.revenue.previous.toFixed(0)}
                </p>
              </div>
            </div>
          </div>

          {/* Appointment cards */}
          <div>
            <h3 className="text-fg-muted mb-3 text-sm font-semibold tracking-wide uppercase">
              Appointments
            </h3>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  Total
                </div>
                <p className="text-fg mt-1 text-2xl font-bold">{data.appointments.total}</p>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted text-sm">By Status</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(
                    Object.entries(data.appointments.statusBreakdown) as [
                      AppointmentStatus,
                      number,
                    ][]
                  ).map(([status, count]) =>
                    count > 0 ? (
                      <span
                        key={status}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                        style={{
                          backgroundColor: `${statusColor[status]}20`,
                          color: statusColor[status],
                        }}
                      >
                        {count} {status}
                      </span>
                    ) : null,
                  )}
                </div>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted text-sm">Completion Rate</div>
                <p className="text-fg mt-1 text-2xl font-bold">
                  {data.appointments.completionRate.toFixed(0)}%
                </p>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted text-sm">Most Popular</div>
                <p className="text-fg mt-1 truncate text-lg font-bold">
                  {data.appointments.mostPopularService}
                </p>
              </div>
            </div>
          </div>

          {/* Client cards */}
          <div>
            <h3 className="text-fg-muted mb-3 text-sm font-semibold tracking-wide uppercase">
              Clients
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4" />
                  Total Unique
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-fg text-2xl font-bold">{data.clients.unique}</p>
                  <TrendBadge value={data.clients.trend} />
                </div>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted text-sm">New</div>
                <p className="text-success mt-1 text-2xl font-bold">{data.clients.new}</p>
              </div>
              <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
                <div className="text-fg-muted text-sm">Returning</div>
                <p className="text-fg mt-1 text-2xl font-bold">{data.clients.returning}</p>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Revenue trend */}
            <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
              <h3 className="text-fg mb-4 text-sm font-bold">Revenue Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.charts.revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axis }} />
                    <YAxis tick={{ fontSize: 10, fill: chart.axis }} />
                    <Tooltip
                      formatter={(value: number) => [`$${value}`, 'Revenue']}
                      contentStyle={{
                        borderRadius: '8px',
                        fontSize: '12px',
                        background: chart.tooltipBg,
                        border: `1px solid ${chart.tooltipBorder}`,
                      }}
                      labelStyle={{ color: chart.axis }}
                      itemStyle={{ color: chart.series }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke={chart.series}
                      fill={chart.series}
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Appointments by service */}
            <div className="border-default bg-surface rounded-xl border p-4 shadow-sm">
              <h3 className="text-fg mb-4 text-sm font-bold">Appointments by Service</h3>
              <div className="h-64">
                {data.charts.appointmentsByService.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data.charts.appointmentsByService}
                      layout="vertical"
                      margin={{ left: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: chart.axis }} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        tick={{ fontSize: 10, fill: chart.axis }}
                        width={100}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '8px',
                          fontSize: '12px',
                          background: chart.tooltipBg,
                          border: `1px solid ${chart.tooltipBorder}`,
                        }}
                        labelStyle={{ color: chart.axis }}
                        itemStyle={{ color: chart.series }}
                        cursor={{ fill: chart.grid, fillOpacity: 0.4 }}
                      />
                      <Bar dataKey="count" fill={chart.series} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-fg-subtle flex h-full items-center justify-center text-sm">
                    No data for this period
                  </div>
                )}
              </div>
            </div>

            {/* Status distribution */}
            <div className="border-default bg-surface rounded-xl border p-4 shadow-sm lg:col-span-2">
              <h3 className="text-fg mb-4 text-sm font-bold">Status Distribution</h3>
              <div className="h-64">
                {data.charts.statusDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.charts.statusDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        stroke={chart.tooltipBg}
                        label={renderPieLabel(chart.axis)}
                        labelLine={{ stroke: chart.axis }}
                      >
                        {data.charts.statusDistribution.map((entry) => (
                          <Cell key={entry.name} fill={statusColor[entry.name]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: '8px',
                          fontSize: '12px',
                          background: chart.tooltipBg,
                          border: `1px solid ${chart.tooltipBorder}`,
                        }}
                        labelStyle={{ color: chart.axis }}
                        itemStyle={{ color: chart.axis }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-fg-subtle flex h-full items-center justify-center text-sm">
                    No data for this period
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
